import { useEffect, useRef, useState } from 'react'

interface UseElementOutOfViewOptions {
  threshold?: number
  rootMargin?: string
}

/**
 * Hook to detect when an element is out of view (completely scrolled past)
 * @param options - Intersection Observer options
 * @returns tuple of [ref to attach to element, isOutOfView boolean]
 */
export const useElementOutOfView = <T extends HTMLElement>(
  options: UseElementOutOfViewOptions = {},
): [React.RefObject<T | null>, boolean] => {
  const elementRef = useRef<T>(null)
  const [isOutOfView, setIsOutOfView] = useState(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry) {
          // Element is out of view when it's not intersecting
          setIsOutOfView(!entry.isIntersecting)
        }
      },
      {
        threshold: options.threshold ?? 0,
        rootMargin: options.rootMargin ?? '0px',
      },
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [options.threshold, options.rootMargin])

  return [elementRef, isOutOfView]
}
